/* eslint-disable no-console */
import {useCallback, useEffect, useRef, useState} from 'react'
import {List, Map} from 'immutable'
import {useQuery} from '@apollo/client'

import {cleanContactsGraphQLResponse, cleanGraphQLResponse} from './graphQLTools'
import {CONTACTS, SCORE} from '../graphql/queries/userData'
import {ITEM_RESPONSES} from '../graphql/queries/itemResponse'
import {RESPONSES_WITH_ITEM} from '../graphql/queries/responsesWithItem'
import {DEPUTIES, SPECIFIC_ITEMS_DEPUTIES, DEPUTIZERS} from '../graphql/queries/deputy'
import {VAULT_EXTENSION} from '../graphql/queries/vaultExtension'
import {pageNumberAndSize, sortValue} from '../lib/summariesTableTools'
import {cleanApiResponseData, debounce, objectsAreEqual} from '../lib/tools'

export const useClientSummariesTableData = readEndpoint => {
  const previousTableStateRef = useRef({})

  const [clientSummaries, setClientSummaries] = useState([])
  const [refetchCheckboxFilterOptions, setRefetchCheckboxFilterOptions] = useState(true)
  const [isFiltering, setIsFiltering] = useState(false)
  const [loading, setLoading] = useState(true)
  const [pages, setPages] = useState(0)
  const [recordCount, setRecordCount] = useState(0)
  const [checkboxFilterOptions, setCheckboxFilterOptions] = useState({
    afpNames: [],
    firmNames: []
  })

  const buildFilterQuery = filtered => {
    if (!filtered || !filtered.length) return ''

    return filtered.reduce((acc, filter) => {
      // eslint-disable-next-line no-undefined
      if (filter.value === null || filter.value === undefined ||
          (Array.isArray(filter.value) && filter.value.length === 0) ||
          filter.value === '')
        return acc

      // Handle array values for checkbox filters
      if (Array.isArray(filter.value) && filter.value.length > 0) {
        const safeValues = filter.value.map(value => value.replace(/,/g, '†'))

        return `${acc}&filter[${filter.id}]=${encodeURIComponent(safeValues.join(','))}`
      }

      return `${acc}&filter[${filter.id}]=${encodeURIComponent(filter.value)}`
    }, '')
  }

  const readEndpointQuery = tableState => {
    const baseQuery = tableState ?
      `${pageNumberAndSize(tableState)}${sortValue(tableState.sorted)}${buildFilterQuery(tableState.filtered)}` :
      ''

    return baseQuery
  }

  const trimAndFilterNameFilter = (filters = []) => {
    const filtersWithTrimmedNameFilter = filters.map(filter => (
      filter.id === 'name' && typeof filter.value === 'string' ?
        {...filter, value: filter.value.trim()} :
        filter
    ))

    return filtersWithTrimmedNameFilter.filter(obj => obj.value !== '')
  }

  const hasTableStateChanged = newTableState => {
    const sortedPreviousTableStateFilters = [...(previousTableStateRef.current.filtered || [])]
      .sort((objA, objB) => objA.id.localeCompare(objB.id))
    const sortedNewTableStateFilters = [...(newTableState.filtered || [])]
      .sort((objA, objB) => objA.id.localeCompare(objB.id))

    const trimmedAndFilteredNewTableStateFilters = trimAndFilterNameFilter(sortedNewTableStateFilters)

    const previousTableStateWithSortedFilters = {
      ...previousTableStateRef.current,
      filtered: sortedPreviousTableStateFilters
    }

    const newTableStateWithTrimmedAndFilteredFilters = {
      ...newTableState,
      filtered: trimmedAndFilteredNewTableStateFilters
    }

    return !objectsAreEqual(previousTableStateWithSortedFilters, newTableStateWithTrimmedAndFilteredFilters)
  }

  const fetchClientSummaries = useCallback(
    debounce(tableState => {
      if (hasTableStateChanged(tableState)) {
        const tableStateWithTrimmedAndFilteredFilters = {
          ...tableState,
          filtered: trimAndFilterNameFilter(tableState.filtered)
        }

        setLoading(true)
        setIsFiltering(tableStateWithTrimmedAndFilteredFilters.filtered?.length > 0)

        readEndpoint(`client-summaries?${readEndpointQuery(tableStateWithTrimmedAndFilteredFilters)}`)
          .then(response => {
            const data = cleanApiResponseData(response.body.data)

            setClientSummaries(data)
            setPages(response.body.meta['page-count'])
            setRecordCount(response.body.meta['record-count'])
          })
          .catch(error => {
            console.error('Error fetching client-summaries:', error)
          })
          .finally(() => {
            setLoading(false)
            previousTableStateRef.current = tableStateWithTrimmedAndFilteredFilters
          })
      }
    }, 500),
    [readEndpoint]
  )

  useEffect(() => {
    if (refetchCheckboxFilterOptions) {
      readEndpoint('client-summaries/unique-dropdown-values')
        .then(response => {
          const data = response.body.data.attributes

          setCheckboxFilterOptions({
            afpNames: data['afp-names'] || [],
            firmNames: data['firm-names'] || []
          })
          setRefetchCheckboxFilterOptions(false)
        })
        .catch(error => {
          console.error('Error fetching unique-dropdown-values:', error)
        })
    }
  }, [readEndpoint, refetchCheckboxFilterOptions])

  return {
    clientSummaries,
    fetchClientSummaries,
    isFiltering,
    loading,
    pages,
    recordCount,
    checkboxFilterOptions,
    setRefetchCheckboxFilterOptions
  }
}

export const useFuneralHomeClientSummariesTableState = fetchClientSummaries => {
  const [filtered, setFiltered] = useState([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [sorted, setSorted] = useState([{id: 'created-at', desc: true}])

  useEffect(() => {
    const tableState = {
      filtered,
      page,
      pageSize,
      sorted
    }

    fetchClientSummaries(tableState)
  }, [fetchClientSummaries, filtered, page, pageSize, sorted])

  const handleFilteredChange = newFilters => {
    setFiltered(newFilters)
    setPage(0)
  }

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize)
    setPage(0)
  }

  const handleSortedChange = newSorted => {
    setSorted(newSorted)
    setPage(0)
  }

  return {
    filtered,
    handleFilteredChange,
    handlePageSizeChange,
    handleSortedChange,
    page,
    pageSize,
    setFiltered,
    setPage,
    sorted
  }
}

/**
 * Used to get the previous value of a state or prop in a functional component.
 *
 * Taken from the react docs (https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state) as the
 * approach for getting previous state or props in functional component, but slightly changed to initialize with the passed in
 * value, otherwise a check between previous and current will always be different on first render.
*/
export const usePrevious = value => {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export const useDeputyData = everplanId => {
  const formattedEverplanId = parseInt(everplanId, 10)

  const {previousData: prevDeputiesData, data: deputiesData} = useQuery(DEPUTIES, {
    skip: Number.isNaN(formattedEverplanId),
    variables: {everplanId: formattedEverplanId}
  })

  const {previousData: prevSpecificItemsDeputiesData, data: specificItemsDeputiesData} = useQuery(SPECIFIC_ITEMS_DEPUTIES)

  const [deputies, setDeputies] = useState(cleanGraphQLResponse(deputiesData, 'deputies'))
  const [specificItemsDeputies, setSpecificItemsDeputies] = useState(cleanGraphQLResponse(specificItemsDeputiesData, 'specificItemsDeputies'))

  useEffect(() => {
    if (prevDeputiesData || deputies.isEmpty())
      setDeputies(cleanGraphQLResponse(deputiesData, 'deputies'))
  }, [prevDeputiesData, deputiesData])

  useEffect(() => {
    if (prevSpecificItemsDeputiesData)
      setSpecificItemsDeputies(cleanGraphQLResponse(specificItemsDeputiesData, 'specificItemsDeputies'))
  }, [prevSpecificItemsDeputiesData, specificItemsDeputiesData])


  return {
    deputies,
    specificItemsDeputies
  }
}

export const useEverplanData = everplanId => {
  const formattedEverplanId = parseInt(everplanId, 10)

  const {
    loading: loadingItemResponses,
    error: itemResponsesError,
    previousData: prevItemResponses,
    data: itemResponsesData,
    refetch: refetchItemResponses
  } = useQuery(ITEM_RESPONSES, {
    variables: {everplanId: formattedEverplanId},
    skip: !everplanId
  })

  const {
    loading: loadingNewContacts,
    error: newContactsError,
    previousData: prevNewContactsData,
    data: newContactsData,
    refetch: refetchNewContacts
  } = useQuery(CONTACTS, {
    variables: {everplanId: formattedEverplanId},
    skip: !everplanId
  })

  const [itemResponses, setItemResponses] = useState(cleanGraphQLResponse(itemResponsesData, 'itemResponses'))
  const [newContacts, setNewContacts] = useState(cleanContactsGraphQLResponse(newContactsData))

  useEffect(() => {
    // When getting another user's everplan for the first time or if there is an error the itemResponses need to be reset.
    // loadingItemResponses is true only when getting another everplan's item responses for the first time.
    if (itemResponsesError || loadingItemResponses)
      return setItemResponses(List())

    // itemResponses is only empty when getting another person's everplan for the first time
    if (prevItemResponses || itemResponses.isEmpty())
      return setItemResponses(cleanGraphQLResponse(itemResponsesData, 'itemResponses'))
  }, [prevItemResponses, itemResponsesData, loadingItemResponses])

  useEffect(() => {
    if (newContactsError || loadingNewContacts)
      return setNewContacts(List())

    if (prevNewContactsData || newContacts.isEmpty())
      return setNewContacts(cleanContactsGraphQLResponse(newContactsData))
  }, [prevNewContactsData, newContactsData, loadingNewContacts])

  return {
    loadingEverplanData: loadingItemResponses || loadingNewContacts,
    itemResponses,
    refetchItemResponses,
    newContacts,
    refetchNewContacts
  }
}

export const useDeputizerData = userConfig => {
  const {previousData: previousDeputizersData, data: deputizersData, loading, refetch} = useQuery(DEPUTIZERS, {variables: {userId: parseInt(userConfig.get('id'), 10)}})

  const [deputizers, setDeputizers] = useState(cleanGraphQLResponse(deputizersData, 'deputizers'))

  useEffect(() => {
    if (previousDeputizersData || deputizers.isEmpty())
      setDeputizers(cleanGraphQLResponse(deputizersData, 'deputizers'))
  }, [previousDeputizersData, deputizersData])

  return {
    loadingDeputizers: loading,
    deputizers,
    refetchDeputizers: refetch
  }
}

export const useVaultExtensionData = everplanId => {
  const formattedEverplanId = parseInt(everplanId, 10)

  const {
    loading: loadingVaultExtension,
    previousData: previousVaultExtensionData,
    data: vaultExtensionData
  } = useQuery(VAULT_EXTENSION, {
    skip: Number.isNaN(formattedEverplanId),
    variables: {everplanId: formattedEverplanId}
  })

  const [vaultExtension, setVaultExtension] = useState(
    cleanGraphQLResponse(vaultExtensionData, 'vaultExtension', Map())
  )

  useEffect(() => {
    if (previousVaultExtensionData || vaultExtension.isEmpty())
      setVaultExtension(cleanGraphQLResponse(vaultExtensionData, 'vaultExtension', Map()))
  }, [previousVaultExtensionData, vaultExtensionData])

  return {
    loadingVaultExtension,
    vaultExtension
  }
}

export const useResponsesWithItem = everplanId => {
  const {previousData, data} = useQuery(RESPONSES_WITH_ITEM, {
    variables: {everplanId: parseInt(everplanId, 10)},
    skip: !everplanId
  })

  const [responsesWithItem, setResponsesWithItem] = useState(cleanGraphQLResponse(data, 'responsesWithItem'))

  useEffect(() => {
    if (previousData || responsesWithItem.isEmpty())
      setResponsesWithItem(cleanGraphQLResponse(data, 'responsesWithItem'))
  }, [previousData, data])

  return responsesWithItem
}

export const useScoreData = userId => {
  const {previousData, data, startPolling, stopPolling, refetch} = useQuery(SCORE, {variables: {userId: parseInt(userId, 10)}})

  const [score, setScore] = useState(cleanGraphQLResponse(data, 'score', Map()))

  useEffect(() => {
    if (previousData || score.isEmpty())
      setScore(cleanGraphQLResponse(data, 'score'))
  }, [previousData, data])

  return {
    refetch,
    startPolling,
    stopPolling,
    score
  }
}
